/**
 * OnboardingBlock — Dismissible step-by-step onboarding card
 *
 * Configurable steps array with title/description/optional image.
 * Dismissible via localStorage (key auto-derived from hub slug).
 * Can be force-shown via forceShow prop (e.g. from scroll nav "How to Play" modal action).
 */

import React, { useState, useCallback } from "react";
import { IoClose, IoArrowForward } from "react-icons/io5";
import "./OnboardingBlock.scss";

interface OnboardingStep {
    title: string;
    description: string;
    imageUrl?: string;
}

interface OnboardingBlockProps {
    config?: {
        title?: string;
        steps: OnboardingStep[];
        dismissible?: boolean;
    };
    surfaceSlug?: string;
    forceShow?: boolean;
    onDismiss?: () => void;
}

export const OnboardingBlock = ({
    config,
    surfaceSlug,
    forceShow = false,
    onDismiss,
}: OnboardingBlockProps) => {
    const storageKey = `onboarding-dismissed-${surfaceSlug || "default"}`;
    const [isDismissed, setIsDismissed] = useState(() => localStorage.getItem(storageKey) === "true");

    const handleDismiss = useCallback(() => {
        localStorage.setItem(storageKey, "true");
        setIsDismissed(true);
        onDismiss?.();
    }, [storageKey, onDismiss]);

    if (!config?.steps?.length) return null;
    if (isDismissed && !forceShow) return null;

    const title = config.title || "How to Play";
    const dismissible = config.dismissible !== false;

    return (
        <div className="onboarding-block">
            <div className="onboarding-block__header">
                <span className="onboarding-block__title">{title}</span>
                {dismissible &&
                    <button
                        className="onboarding-block__close"
                        onClick={handleDismiss}
                        aria-label="Dismiss"
                        type="button"
                    >
                        <IoClose />
                    </button>
                }
            </div>

            <div className="onboarding-block__steps">
                {config.steps.map((step, index) =>
                    <React.Fragment key={step.title}>
                        <div className="onboarding-block__step">
                            {step.imageUrl
                                ? <img
                                    className="onboarding-block__step-image"
                                    src={step.imageUrl}
                                    alt={step.title}
                                />
                                : <div className="onboarding-block__step-number">
                                    {index + 1}
                                </div>
                            }
                            <div className="onboarding-block__step-content">
                                <h4 className="onboarding-block__step-title">{step.title}</h4>
                                <p className="onboarding-block__step-desc">{step.description}</p>
                            </div>
                        </div>
                        {index < config.steps.length - 1 &&
                            <div className="onboarding-block__arrow">
                                <IoArrowForward />
                            </div>
                        }
                    </React.Fragment>)}
            </div>
        </div>
    );
};

export default OnboardingBlock;
